import clsx from 'clsx';
import { atom, useAtom } from 'jotai';
import {
  type Control,
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';

import type { Paginate } from '@quick/go-api-types/common';
import type { ICity } from '@quick/go-api-types/services/cities';

import { useAppTranslation } from '@/app/i18n/client';
import { ReactSelect as RSelect } from '@/components/core/ReactSelect';
import { useDebounce } from '@/layers/core/deal/services/useDebounce';
import { useFetch } from '@/layers/core/deal/services/useFetch';

const search = atom('');
export function AutocompleteCities<T extends FieldValues>({
  url,
  params,
  nameFilter,
  rules,
  control,
  className,
}: {
  url?: string;
  params?: Record<string, string | number | undefined>;
  nameFilter?: FieldPath<T>;
  rules?: ControllerProps<T>['rules'];
  control?: Control<T>;
  className?: string;
}) {
  const [citySearch, setCitySearch] = useAtom(search);
  const debouncedSearch = useDebounce(citySearch.trim(), 1_000);

  const { data: citiesData, isLoading } = useFetch<Paginate<ICity>>(
    {
      url: url ?? '/services/cities',
      params: {
        pagination: true,
        page_size: 20,
        ...params,
        city_name: debouncedSearch || params?.city_name,
      },
    },
    {
      keepPreviousData: true,
    }
  );
  const { t } = useAppTranslation(['common', 'ServicesOpPl']);

  return (
    <Controller
      name={nameFilter ?? ('city' as FieldPath<T>)}
      rules={rules}
      control={control}
      render={({ field }) => (
        <RSelect
          {...field}
          classNamePrefix="rs"
          className={clsx(className, 'rs-container w-40')}
          isLoading={isLoading}
          isSearchable
          noOptionsMessage={({ inputValue }) => {
            if (inputValue.trim().length < 3)
              return t('common:msg.writeSearch');
            return t('common:msg.noResults');
          }}
          options={citiesData?.data ?? []}
          value={citiesData?.data.find(
            ({ id }) => id.toString() === field?.value?.toString()
          )}
          getOptionValue={(c) => c.id.toString()}
          getOptionLabel={(c) =>
            `${c.name}, ${c.country_state.name}, ${c.country_state.country.name}`
          }
          onChange={(e, action) => {
            if (action.action === 'clear') field.onChange('');
            if (!e) return;
            field.onChange(e.id);
          }}
          isClearable
          styles={{
            noOptionsMessage: (provided) => ({
              ...provided,
              background: '#ccc',
            }),
            menuPortal: (provided) => ({
              ...provided,
              zIndex: 10,
            }),
          }}
          placeholder={t('common:select_option')}
          onInputChange={(v, meta) => {
            if (meta.action === 'input-change') {
              setCitySearch(v);
            }
          }}
        />
      )}
    />
  );
}
